import i18next from 'i18next';
import type { ChangeEvent, FormEvent, FunctionComponent, PropsWithChildren } from 'react';
import { useState } from 'react';

import { Button } from '~/ui/core/button/Button';
import { Input } from '~/ui/core/input/Input';

import { ErrorPage } from './design/components/errors/ErrorPage';
import { TenantNotFoundError } from './legacy/customization';

type FatalErrorFallbackProps = PropsWithChildren<{
    readonly message?: string;
    readonly title?: string;
}>;

export const FatalErrorFallback: FunctionComponent<FatalErrorFallbackProps> = ({
    children,
    message = 'Unfortunately, the application could not start. Please reload the page and try again.',
    title = 'Something went wrong',
}) => (
    <ErrorPage message={message} title={title}>
        {children}
    </ErrorPage>
);

const getStartupTranslation = (key: string, fallback: string): string =>
    i18next.isInitialized ? i18next.t(key) : fallback;

const tenantDomainSuffix = '.cobrainer.ai';

const getTenantUrl = (tenantSlug: string): string | undefined => {
    try {
        return new URL(`https://${encodeURIComponent(tenantSlug.trim())}${tenantDomainSuffix}`).origin;
    } catch (_: unknown) {
        return undefined;
    }
};

const TenantRedirectForm: FunctionComponent = () => {
    const [tenantSlug, setTenantSlug] = useState('');
    const tenantUrl = getTenantUrl(tenantSlug);
    const handleTenantChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setTenantSlug(event.currentTarget.value);
    };
    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();

        if (tenantUrl !== undefined) {
            globalThis.location.href = tenantUrl;
        }
    };

    return (
        <form
            aria-label={getStartupTranslation('errors.tenant-not-found.form-label', 'Tenant URL')}
            action={tenantUrl}
            className="flex w-full flex-col gap-m sm:flex-row"
            onSubmit={handleSubmit}
        >
            <Input
                required={true}
                aria-label={getStartupTranslation('errors.tenant-not-found.input-label', 'Tenant name')}
                autoCapitalize="none"
                autoComplete="organization"
                className="min-w-0 flex-1"
                endElement={<span className="whitespace-nowrap text-text-secondary">{tenantDomainSuffix}</span>}
                placeholder={getStartupTranslation('errors.tenant-not-found.placeholder', 'your-tenant')}
                spellCheck={false}
                value={tenantSlug}
                onChange={handleTenantChange}
            />
            <Button className="shrink-0" type="submit" variant="primary">
                {getStartupTranslation('errors.tenant-not-found.action', 'Continue')}
            </Button>
        </form>
    );
};

export const StartupErrorFallback: FunctionComponent<{ readonly error: unknown }> = ({ error }) => {
    if (error instanceof TenantNotFoundError) {
        return (
            <FatalErrorFallback
                message={getStartupTranslation(
                    'errors.tenant-not-found.message',
                    'This tenant does not exist under this URL. Enter your tenant name to continue.',
                )}
                title={getStartupTranslation('errors.tenant-not-found.title', 'Tenant not found')}
            >
                <TenantRedirectForm />
            </FatalErrorFallback>
        );
    }

    if (i18next.isInitialized) {
        return (
            <FatalErrorFallback message={i18next.t('errors.fatal.message')} title={i18next.t('errors.fatal.title')} />
        );
    }

    return <FatalErrorFallback />;
};
